import { appRoutes } from "@/app/router";
import { CleanableNote } from "@/modules/keyboard-shortcuts/types";
import { Pane, Tab } from "@/store/navigation/types";
import { matchPath } from "react-router";

export const MAIN_PANEL_SPLIT_VIEW_PANEL_GROUP_ID = "main-panel-split-view-panel-group";

export const isCleanableNote = (note: unknown): note is CleanableNote =>
  typeof note === "object" &&
  note !== null &&
  "cleanup" in note &&
  typeof note.cleanup === "function";

export const focusPaneForKeyboardInteraction = (paneId: string) => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  window.requestAnimationFrame(() => {
    const paneElement = document.getElementById(paneId);
    if (!(paneElement instanceof HTMLElement)) {
      return;
    }

    const editableElement = paneElement.querySelector<HTMLElement>('[contenteditable="true"]');
    if (editableElement) {
      editableElement.focus();
      return;
    }

    const previousTabIndex = paneElement.getAttribute("tabindex");
    if (!paneElement.hasAttribute("tabindex")) {
      paneElement.setAttribute("tabindex", "-1");
    }

    paneElement.focus();

    if (previousTabIndex === null) {
      paneElement.removeAttribute("tabindex");
    }
  });
};

export const isNotesListPath = (pathname: string) =>
  pathname === appRoutes.notesList({}).path ||
  !!matchPath(
    appRoutes.importBatchNotes({ params: { importBatchId: ":importBatchId" } }).path,
    pathname
  );

export const getMainPanelSplitViewContainerWidth = (): number | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }

  const element = document.getElementById(MAIN_PANEL_SPLIT_VIEW_PANEL_GROUP_ID);
  if (!element) {
    return undefined;
  }

  return element.getBoundingClientRect().width;
};

export const getVisiblePaneTabs = ({
  activeTabId,
  panesMap,
  tabsMap,
  getResponsivePanes,
  containerWidth,
}: {
  activeTabId?: string;
  panesMap?: {
    size: number;
    values(): IterableIterator<{ activeTabId: string | null }>;
  };
  tabsMap: {
    get(key: string): Tab | undefined;
  };
  getResponsivePanes?: (containerWidth: number) => {
    visiblePanes: Array<{ activeTabId: string | null }>;
  };
  containerWidth?: number;
}) => {
  if (typeof containerWidth === "number" && containerWidth > 0 && getResponsivePanes) {
    return getResponsivePanes(containerWidth)
      .visiblePanes.flatMap(pane => (pane.activeTabId ? [tabsMap.get(pane.activeTabId)] : []))
      .filter((tab): tab is Tab => !!tab);
  }

  if (panesMap && panesMap.size > 0) {
    return Array.from(panesMap.values())
      .flatMap(pane => (pane.activeTabId ? [tabsMap.get(pane.activeTabId)] : []))
      .filter((tab): tab is Tab => !!tab);
  }

  const activeTab = activeTabId ? tabsMap.get(activeTabId) : undefined;
  return activeTab ? [activeTab] : [];
};

export const getRelativeTabShortcutTabs = ({
  activePaneId,
  containerWidth,
  getResponsivePanes,
  getTabsForPane,
  panes,
  panesMap,
}: {
  activePaneId: string;
  containerWidth?: number;
  getResponsivePanes?: (containerWidth: number) => {
    hiddenPanes: Array<Pick<Pane, "id">>;
  };
  getTabsForPane: (paneId: string) => Tab[];
  panes?: Array<Pick<Pane, "id" | "order">>;
  panesMap?: {
    values(): IterableIterator<Pick<Pane, "id" | "order">>;
  };
}): Tab[] => {
  const activePaneTabs = getTabsForPane(activePaneId);
  const responsivePanes =
    typeof containerWidth === "number" && containerWidth > 0 && getResponsivePanes
      ? getResponsivePanes(containerWidth)
      : undefined;

  if (!responsivePanes?.hiddenPanes.length) {
    return activePaneTabs;
  }

  const orderedPanes = [...(panes ?? (panesMap ? Array.from(panesMap.values()) : []))].sort(
    (firstPane, secondPane) => firstPane.order - secondPane.order
  );

  if (orderedPanes.length === 0) {
    return activePaneTabs;
  }

  const activePaneDefaultTab = activePaneTabs.find(tab => tab.isDefault);
  const flattenedNonDefaultTabs = orderedPanes.flatMap(pane =>
    getTabsForPane(pane.id).filter(tab => !tab.isDefault)
  );

  return activePaneDefaultTab
    ? [activePaneDefaultTab, ...flattenedNonDefaultTabs]
    : flattenedNonDefaultTabs;
};
